import { useCallback, useState } from 'react'
import { ITask } from '@common/interfaces/task'
import WarningModal from '../../components/WarningModal'
import {
  CompleteProceed,
  ICompleteConfirm,
  completeOptionsFor,
  singleCompleteWarning,
} from './taskHelpers'

/**
 * The single-task completion gate: when the task still has open subtasks or an
 * unfinished linked record, park the completion behind a WarningModal; otherwise
 * run it directly. Render `completeConfirmModal` once in the host.
 */
export const useTaskCompleteConfirm = () => {
  const [completeConfirm, setCompleteConfirm] = useState<ICompleteConfirm | null>(null)
  const [isConfirmCompleting, setIsConfirmCompleting] = useState(false)

  // Resolves with `proceed`'s result when no warning is needed, so a caller can see a refused write.
  const requestTaskComplete = useCallback(async (task: ITask, proceed: CompleteProceed) => {
    const warning = singleCompleteWarning(task)
    if (warning) {
      setCompleteConfirm({ message: warning, proceed, options: completeOptionsFor([task]) })
      return
    }
    return proceed({})
  }, [])

  const handleCompleteConfirm = useCallback(async () => {
    if (!completeConfirm) return
    setIsConfirmCompleting(true)
    try {
      await completeConfirm.proceed(completeConfirm.options ?? {})
    } finally {
      setIsConfirmCompleting(false)
      setCompleteConfirm(null)
    }
  }, [completeConfirm])

  const handleCompleteCancel = useCallback(() => setCompleteConfirm(null), [])

  const completeConfirmModal = completeConfirm && (
    <WarningModal
      title={completeConfirm.title ?? 'Complete this task?'}
      warningMessage={completeConfirm.message}
      onConfirm={handleCompleteConfirm}
      onCancel={handleCompleteCancel}
      confirmText={completeConfirm.confirmText ?? 'Complete'}
      cancelText="Cancel"
      disabled={isConfirmCompleting}
      isLoading={isConfirmCompleting}
    />
  )

  return { requestTaskComplete, setCompleteConfirm, completeConfirmModal }
}
